import { useEffect, useMemo, useRef, useState } from "react";
import { GfgSelect } from "@/components/ui/GfgSelect";
import { WELL_TOLD_HIDDEN_STATE_ABBRS } from "@/constants/redemption";
import { usePublicConfig } from "@/hooks/usePublicConfig";
import type { ProductCustomLocation } from "@/interfaces/product";
import type { CustomLocation, WellToldPreviewFetcher } from "@/interfaces/redemption";
import { getRegionOptions } from "@/utils/address-profiles";
import { loadGooglePlaces } from "@/utils/googleMaps";
import { buildPreviewKey, formatCoords, loadWellToldAutocomplete } from "@/utils/wellTold";

interface RedemptionLocationPickerProps {
  /** The product's custom-location requirement (from the detail endpoint). */
  location: ProductCustomLocation;
  value: CustomLocation | null;
  /** Label of the chosen colour swatch — fills the preview key's `{color}` placeholder. */
  colorLabel?: string;
  onChange: (value: CustomLocation | null) => void;
  /** Rendered Well Told preview photos for the chosen place (null ⇒ none / cleared). */
  onPreview?: (imageUrls: string[] | null) => void;
}

type WidgetStatus = "loading" | "ready" | "failed";

const LABEL = "font-lato text-[13px] font-bold uppercase tracking-[1.2px] text-rodeo-black";
const NOTE = "mt-1.5 font-lato text-[12px] text-graphite";

/**
 * The custom-location step for a Well Told map product (v1: the "Choose your Custom
 * Location" block in gift-detail-2.ejs). Two modes:
 *   - `hometown` — Well Told's own autocomplete widget (their normalisation rules + preview
 *     photos), mounted into a host div. The Places SDK is loaded through our async loader
 *     first so the widget reuses it instead of injecting a second Maps script.
 *   - `state` — a US state dropdown (DC hidden — no state map exists for it).
 * The selection is required: the slide-in's CTA stays disabled until `value` is set, and
 * the API refuses a map product without it.
 */
export default function RedemptionLocationPicker({
  location,
  value,
  colorLabel,
  onChange,
  onPreview,
}: RedemptionLocationPickerProps) {
  const { data: config } = usePublicConfig();
  const apiKey = config?.googleMapsApiKey;
  const hostRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<WidgetStatus>("loading");
  const [previewNote, setPreviewNote] = useState<string | null>(null);
  // The widget's preview fetcher for the current place; re-run when the colour changes.
  const fetcherRef = useRef<WellToldPreviewFetcher | null>(null);
  // Latest callbacks/inputs without re-mounting the (one-shot) widget.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onPreviewRef = useRef(onPreview);
  onPreviewRef.current = onPreview;
  const previewKey = buildPreviewKey(location.previewKey, colorLabel);
  const previewKeyRef = useRef(previewKey);
  previewKeyRef.current = previewKey;
  const previewRun = useRef(0);

  const refreshPreview = async () => {
    const fetcher = fetcherRef.current;
    const key = previewKeyRef.current;
    const run = ++previewRun.current;
    if (!fetcher || !key) {
      setPreviewNote(null);
      onPreviewRef.current?.(null);
      return;
    }
    const urls = await fetcher(key).catch(() => [] as string[]);
    if (run !== previewRun.current) return; // a newer place/colour superseded this
    setPreviewNote(urls.length ? null : "Sorry, no preview available.");
    onPreviewRef.current?.(urls.length ? urls : null);
  };

  const isHometown = location.mode === "hometown";

  // refreshPreview reads everything through refs, so it is stable for this one-shot mount.
  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshPreview is ref-backed
  useEffect(() => {
    if (!isHometown) return;
    const host = hostRef.current;
    if (!apiKey || !host) {
      if (config && !apiKey) setStatus("failed"); // config loaded but no key configured
      return;
    }
    let cancelled = false;
    setStatus("loading");
    (async () => {
      await loadGooglePlaces(apiKey);
      const WellToldAutocomplete = await loadWellToldAutocomplete();
      if (cancelled) return;
      if (!WellToldAutocomplete) {
        setStatus("failed");
        return;
      }
      const el = new WellToldAutocomplete({
        apiKey,
        name: "hometown",
        required: true,
        onPlaceSelected: (place, getProductImageURLs) => {
          const { lat, lng } = place.coordinates;
          onChangeRef.current({
            mode: "hometown",
            hometown: place.name,
            coords: formatCoords(lat, lng),
          });
          fetcherRef.current = getProductImageURLs;
          void refreshPreview();
        },
      });
      host.replaceChildren(el);
      setStatus("ready");
    })();
    return () => {
      cancelled = true;
      host.replaceChildren();
      fetcherRef.current = null;
    };
  }, [apiKey, config, isHometown]);

  // A colour change re-renders the preview for the already-chosen place.
  // biome-ignore lint/correctness/useExhaustiveDependencies: previewKey is the trigger; refreshPreview is ref-backed
  useEffect(() => {
    if (fetcherRef.current) void refreshPreview();
  }, [previewKey]);

  const stateOptions = useMemo(
    () =>
      getRegionOptions("US")
        .filter((r) => !WELL_TOLD_HIDDEN_STATE_ABBRS.includes(r.abbr))
        .map((r) => ({ value: r.value, label: r.label })),
    [],
  );

  if (!isHometown) {
    return (
      <div data-testid="redemption-location-picker" data-mode="state">
        <p className={`${LABEL} mb-3`}>Choose your state</p>
        <GfgSelect
          label="State"
          value={value?.mode === "state" ? value.state : ""}
          onChange={(e) =>
            onChange(e.target.value ? { mode: "state", state: e.target.value } : null)
          }
          options={[{ value: "", label: "Select a state", disabled: true }, ...stateOptions]}
          ignorePasswordManagers
        />
      </div>
    );
  }

  return (
    <div data-testid="redemption-location-picker" data-mode="hometown" data-status={status}>
      <fieldset className="m-0 border-0 p-0">
        <legend className={`${LABEL} mb-3`}>Choose your custom location</legend>
        {/* The Well Told widget renders its own labelled input into this host. */}
        <div ref={hostRef} className="wt-autocomplete-host" />
      </fieldset>
      {status === "loading" && <p className={NOTE}>Loading location search…</p>}
      {status === "failed" && (
        <p className={`${NOTE} text-good-wine`} role="alert">
          We couldn't load the location search. Please refresh the page and try again.
        </p>
      )}
      {previewNote && (
        <p className={NOTE} aria-live="polite">
          {previewNote}
        </p>
      )}
    </div>
  );
}
